import React, { memo, useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  IconChevronDown,
  IconChevronRight,
  IconUser,
  IconPlus,
  IconArrowLeft,
  IconTrash,
  IconDotsVertical,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  Badge,
  Loader,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Group,
  Box,
  Flex,
  Menu,
  Tooltip,
} from "@mantine/core";
import fetchApi from "../Lib/api";
import useStartupStore from "../../stores/startupStore";
import { select } from "@syncfusion/ej2-base";

const MobileContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const StickyHeader = styled.div`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f3f4f6;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #6c45e7;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 0;
  cursor: pointer;
  flex-shrink: 0;

  &:active {
    opacity: 0.7;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuButton = styled.button`
  background: var(--color-container-card-default);
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-container-card-hover);
  }

  &:active {
    opacity: 0.8;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 4px 0 0 0;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ColumnSection = styled.div`
  background: white;
  margin: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  cursor: pointer;
  border-left: 4px solid ${(props) => props.color};
  background: ${(props) => (props.expanded ? "#f9fafb" : "white")};
  transition: background 0.2s;

  &:active {
    background: #f3f4f6;
  }
`;

const ColumnHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const ColumnDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(props) => props.color};
  flex-shrink: 0;
`;

const ColumnTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`;

const TaskCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
`;

const TaskList = styled.div`
  padding: 0 16px 16px;
`;

const TaskCard = styled.div`
  padding: 12px;
  margin-bottom: 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    transform: scale(0.98);
    background: #f9fafb;
  }
`;

const TaskTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a1a1a;
`;

const TaskDescription = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const TaskMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TaskTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${(props) => props.color};
  color: white;
`;

const TaskSubcategory = styled.div`
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AssigneeSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AssigneeAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #6c45e7;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
`;

const NoTasks = styled.div`
  padding: 24px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

const FloatingAddButton = styled.button`
  position: fixed;
  bottom: 48px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #6c45e7;
  border: none;
  box-shadow: 0 4px 12px rgba(108, 69, 231, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: all 0.2s;

  &:active {
    transform: scale(0.95);
  }

  svg {
    color: white;
  }
`;

const PriorityBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${(props) =>
    props.priority === "High"
      ? "#fee2e2"
      : props.priority === "Medium"
      ? "#fef3c7"
      : "#dbeafe"};
  color: ${(props) =>
    props.priority === "High"
      ? "#991b1b"
      : props.priority === "Medium"
      ? "#92400e"
      : "#1e40af"};
`;

const BoardTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const BoardTab = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: ${(props) => (props.active ? "#6c45e7" : "#f0f0f0")};
  color: ${(props) => (props.active ? "white" : "#666")};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  flex-shrink: 0;

  &:active {
    opacity: 0.8;
  }
`;

const AddBoardButton = styled.button`
  padding: 8px 14px;
  border: 1px dashed #6c45e7;
  border-radius: 8px;
  background: transparent;
  color: #6c45e7;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:active {
    opacity: 0.7;
  }
`;

const ColumnActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #6c45e7;
    box-shadow: 0 0 0 3px rgba(108, 69, 231, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #6c45e7;
    box-shadow: 0 0 0 3px rgba(108, 69, 231, 0.1);
  }
`;

const HelperText = styled.span`
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const DeleteBoardButton = styled.button`
  margin-left: auto;
  padding: 12px;
`;

const ColumnModal = memo(function ColumnModal({
  opened,
  columnName,
  onChange,
  onSave,
  onClose,
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add Column"
      size="lg"
      padding="xl"
      styles={{
        title: { fontSize: "1.5rem", fontWeight: 600 },
        modal: { minHeight: "200px" },
      }}
    >
      <Box p="md">
        <TextInput
          label="Column Name"
          placeholder="Enter column name"
          value={columnName}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          size="lg"
          mb="xl"
          styles={{
            label: {
              fontSize: "1.1rem",
              marginBottom: "0.5rem",
              fontWeight: 500,
            },
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
              minHeight: "3.5rem",
            },
          }}
        />
        <Button
          fullWidth
          size="lg"
          onClick={onSave}
          styles={{
            root: { height: "3.5rem", fontSize: "1.1rem" },
          }}
        >
          Save
        </Button>
      </Box>
    </Modal>
  );
});
const MobileTaskBoardView = ({ subCategoryId, subcategoryName, onBack }) => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedColumns, setExpandedColumns] = useState({});
  const [taskModal, setTaskModal] = useState(null);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const { users, isOwner, isMember, isSubmitted } = useStartupStore(
    (state) => ({
      users: state.members,
      isOwner: state.isOwner,
      isMember: state.isMember,
      isSubmitted: state.isSubmitted,
    })
  );

  const [selectedBoardIndex, setSelectedBoardIndex] = useState(0);
  const [newBoardModal, setNewBoardModal] = useState(false);
  const [newBoardData, setNewBoardData] = useState({
    boardTitle: "",
    boardDescription: "",
  });
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [editingColumnName, setEditingColumnName] = useState({});

  const colors = [
    "#FF6B6B",
    "#FFE66D",
    "#45B7D5",
    "#0251cc",
    "#ea9713",
    "#8e4399",
    "#63ba3c",
    "#4ECDC4",
  ];

  useEffect(() => {
    fetchBoards();
  }, [subCategoryId]);

  const fetchBoards = async () => {
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards`,
        "GET"
      );
      if (!response.ok) throw new Error("Failed to fetch boards");
      const data = await response.json();
      setBoards(data);

      // Auto-expand all columns initially
      if (data.length > 0) {
        const initialExpanded = {};
        data.forEach((board) => {
          board.columnOrder.forEach((colId) => {
            initialExpanded[colId] = true;
          });
        });
        setExpandedColumns(initialExpanded);
      }
    } catch (error) {
      console.error("Error fetching boards:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (columnId) => {
    setExpandedColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const buildUpdatePayload = (task, columnId) => ({
    task_board: {
      columns_attributes: [
        {
          id: columnId,
          tasks_attributes: [
            {
              id: task.id,
              title: task.title,
              description: task.description,
              priority: task.priority,
              user_ids: task.assignees?.map((a) => a.id) || [],
              tags_attributes:
                task.tags?.map((tag) => ({
                  id: tag.id,
                  name: tag.name,
                  color: tag.color,
                })) || [],
            },
          ],
        },
      ],
    },
  });

  const buildAddPayload = (task, columnId) => ({
    task_board: {
      columns_attributes: [
        {
          id: columnId,
          tasks_attributes: [
            {
              title: task.title,
              description: task.description,
              priority: task.priority,
              user_ids: task.assignees?.map((a) => a.id) || [],
              tags_attributes:
                task.tags?.map((tag) => ({
                  id: tag.id,
                  name: tag.name,
                  color: tag.color,
                })) || [],
            },
          ],
        },
      ],
    },
  });

  const handleTaskClick = (task, columnId, boardId) => {
    if (isSubmitted) return;

    setTaskModal({
      mode: "edit",
      task,
      columnId,
      boardId,
    });
  };

  const handleAddTask = () => {
    if (isSubmitted) return;
    // Get the first column of the selected board
    const currentBoard = boards[selectedBoardIndex];
    const firstColumnId = currentBoard?.columnOrder[0];
    setTaskModal({
      mode: "add",
      task: {
        title: "",
        description: "",
        priority: "Medium",
        assignees: [],
        tags: [],
      },
      columnId: firstColumnId || null,
      boardId: currentBoard?.id || null,
    });
  };

  const closeModal = () => {
    setTaskModal(null);
  };

  const onTaskModalChange = ({ field, value }) => {
    setTaskModal((prev) => ({
      ...prev,
      task: { ...prev.task, [field]: value },
    }));
  };

  const moveTask = async (taskId, newColumnId) => {
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${boards[selectedBoardIndex].id}/columns/${newColumnId}/tasks/${taskId}/move`,
        "POST"
      );
      if (!response.ok) {
        console.error("Failed to move task");
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error moving task:", error);
      return false;
    }
  };

  const saveTask = async () => {
    if (!taskModal) return;

    const { mode, task, boardId, columnId, newColumnId } = taskModal;

    if (mode === "edit" || mode === "add") {
      try {
        const payload =
          mode == "add"
            ? buildAddPayload(task, columnId)
            : buildUpdatePayload(task, columnId);

        const response = await fetchApi(
          `/sub_categories/${subCategoryId}/task_boards/${boardId}`,
          "PUT",
          payload
        );


        if (!response.ok) throw new Error("Failed to update task");

        if (mode === "edit" && newColumnId && newColumnId !== columnId) {
          await moveTask(task.id, newColumnId);
        }
    
        closeModal();
        fetchBoards();
      } catch (err) {
        console.error("Update failed:", err);
      }
      return;
    }

    closeModal();
    fetchBoards();
  };

  const updateData = async (newColumns, newColumnOrder) => {
    const currentBoard = boards[selectedBoardIndex];
    const boardId = currentBoard.id;
  
    try {
      const payload = {
        task_board: {
          board_title: currentBoard.boardTitle,
          board_description: currentBoard.boardDescription,
          columns_attributes: [
            // Include existing columns
            ...newColumnOrder.map((colId, index) => {
              const col = newColumns[colId];
              if (col._destroy) return null; // Skip destroyed columns
              
              return {
                id: typeof col.id === "string" && col.id.startsWith("column-")
                  ? undefined
                  : col.id,
                name: col.name,
                color: col.color,
                position: index,
                tasks_attributes: col.tasks?.map((task) => ({
                  id: task.id,
                  title: task.title,
                  description: task.description,
                  priority: task.priority,
                  column_id: col.id,
                  user_ids: task.assignees?.map((assignee) => assignee.id) || [],
                  tags_attributes: task.tags?.map((tag) => ({
                    id: tag.id,
                    name: tag.name,
                    color: tag.color,
                  })) || [],
                })) || [],
              };
            }).filter(Boolean),
            // Include columns marked for destruction
            ...Object.keys(newColumns)
              .filter(colId => newColumns[colId]._destroy)
              .map(colId => ({
                id: newColumns[colId].id,
                _destroy: true,
              })),
          ],
        },
      };
  
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${boardId}`,
        "PUT",
        payload
      );
  
      if (!response.ok) {
        console.error("Failed to update board data");
        return;
      }
  
      await fetchBoards(); // Refresh boards from backend
    } catch (error) {
      console.error("Error updating board data:", error);
    }
  };

  const deleteTask = async () => {
    // Implement delete logic here
    const { task, boardId, columnId } = taskModal;
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${boardId}/columns/${columnId}/tasks/${task.id}`,
        "DELETE"
      );
      if (!response.ok) {
        console.error("Failed to delete task");
        return;
      }
    } catch (error) {
      console.log("error in deleting task ", error);
    }
    closeModal();
    fetchBoards();
  };

  /* ====================
   HELPER: Default Board Data
==================== */
  const getDefaultBoardData = () => {
    const columnsAttributes = [
      { name: "To Do", color: "#0251cc", position: 0, tasks_attributes: [] },
      {
        name: "In Progress",
        color: "#ea9713",
        position: 1,
        tasks_attributes: [],
      },
      { name: "Done", color: "#63ba3c", position: 2, tasks_attributes: [] },
    ];
    return {
      board_title: "Task Board Title",
      board_description:
        "Provide a full description covering on how the tasks are to be handle.",
      columns_attributes: columnsAttributes,
    };
  };

  const handleCreateNewBoard = () => {
    setNewBoardModal(true);
  };

  const closeNewBoardModal = () => {
    setNewBoardModal(false);
    setNewBoardData({
      boardTitle: "",
      boardDescription: "",
    });
  };

  const handleNewBoardChange = (field, value) => {
    setNewBoardData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const confirmCreateNewBoard = async () => {
    const boardPayload = {
      board_title: newBoardData.boardTitle.trim() || "Task Board",
      board_description:
        newBoardData.boardDescription.trim() ||
        "Provide a full description covering on how the tasks are to be handle.",
      columns_attributes: [
        { name: "To Do", color: "#0251cc", position: 0, tasks_attributes: [] },
        {
          name: "In Progress",
          color: "#ea9713",
          position: 1,
          tasks_attributes: [],
        },
        { name: "Done", color: "#63ba3c", position: 2, tasks_attributes: [] },
      ],
    };

    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards`,
        "POST",
        {
          task_board: boardPayload,
        }
      );

      if (!response.ok) {
        throw new Error("Error creating board");
      }

      const data = await response.json();
      const updatedBoards = [...boards, data];
      setBoards(updatedBoards);
      setSelectedBoardIndex(updatedBoards.length - 1);
      closeNewBoardModal();
    } catch (error) {
      console.error("Error creating board:", error);
    }
  };

  const deleteBoard = async () => {
    if (!window.confirm("Are you sure you want to delete this board?")) return;
    const boardToDelete = boards[selectedBoardIndex].id;
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${boardToDelete}`,
        "DELETE"
      );
      if (!response.ok) {
        console.error("Failed to delete board");
        return;
      }
      console.log("Board deleted successfully");
      // If provided, call the parent's callback to update the board list or redirect.
      setBoards(boards.filter((board) => board.id !== boardToDelete));
      setSelectedBoardIndex(0);
    } catch (error) {
      console.error("Error deleting board:", error);
    }
  };

  const saveNewColumn = async () => {
    if (!newColumnName.trim()) {
      return alert("Column name cannot be empty");
    }

    const currentBoard = boards[selectedBoardIndex];
    const newColumnId = `column-${Date.now()}`;

    // Build new columns object
    const newColumns = {
      ...currentBoard.columns,
      [newColumnId]: {
        id: newColumnId, // Temporary ID
        name: newColumnName.trim(),
        color: colors[currentBoard.columnOrder.length % colors.length], // Cycle through colors
        tasks: [],
      },
    };

    // Build new column order
    const newColumnOrder = [...currentBoard.columnOrder, newColumnId];

    // Update backend
    await updateData(newColumns, newColumnOrder);

    // Clear modal state
    setNewColumnName("");
    setShowColumnModal(false);
  };

  const getAllColumns = (boardIndex = selectedBoardIndex) => {
    if (!boards[boardIndex]) return [];

    const board = boards[boardIndex];
    const allColumns = [];

    board.columnOrder.forEach((colId) => {
      const column = board.columns[colId];
      if (column) {
        allColumns.push({
          ...column,
          boardId: board.id,
          boardTitle: board.boardTitle || `Board ${boardIndex + 1}`,
        });
      }
    });
    return allColumns;
  };

  const handleStartEditingColumns = () => {
    // Initialize editing state with current column names
    const initialEditingState = {};
    const allColumns = getAllColumns(selectedBoardIndex);
    allColumns.forEach(column => {
      initialEditingState[column.id] = column.name;
    });
    setEditingColumnName(initialEditingState);
    setIsEditingColumns(true);
  };
  
  const handleSaveColumnEdits = async () => {
    const currentBoard = boards[selectedBoardIndex];
    
    // Build updated columns with new names
    const updatedColumns = { ...currentBoard.columns };
    let hasChanges = false;
  
    Object.keys(editingColumnName).forEach(columnId => {
      const newName = editingColumnName[columnId];
      const oldName = currentBoard.columns[columnId]?.name;
      
      if (newName && newName !== oldName) {
        updatedColumns[columnId] = {
          ...updatedColumns[columnId],
          name: newName
        };
        hasChanges = true;
      }
    });
  
    if (hasChanges) {
      await updateData(updatedColumns, currentBoard.columnOrder);
    }
  
    // Reset editing state
    setIsEditingColumns(false);
    setEditingColumnName({});
  };
  
  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm("Are you sure you want to delete this column? All tasks in this column will be lost.")) {
      return;
    }
  
    const currentBoard = boards[selectedBoardIndex];
    
    // Remove column from order and mark for destruction
    const newColumnOrder = currentBoard.columnOrder.filter(id => id !== columnId);
    const updatedColumns = { ...currentBoard.columns };
    
    // Mark column for destruction if it has an ID (not temporary)
    if (updatedColumns[columnId] && updatedColumns[columnId].id) {
      updatedColumns[columnId] = {
        ...updatedColumns[columnId],
        _destroy: true
      };
    } else {
      // If it's a temporary column, just remove it
      delete updatedColumns[columnId];
    }
  
    await updateData(updatedColumns, newColumnOrder);
    
    // Update local state immediately
    setExpandedColumns(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[columnId];
      return newExpanded;
    });
  };



  if (loading) {
    return (
      <MobileContainer>
        <StickyHeader>
          <HeaderTop>
            <BackButton onClick={onBack}>
              <IconArrowLeft size={14} />
              Back
            </BackButton>
            <HeaderTitle>Loading...</HeaderTitle>
          </HeaderTop>
        </StickyHeader>
        <div
          style={{ display: "flex", justifyContent: "center", padding: "48px" }}
        >
          <Loader color="indigo" />
        </div>
      </MobileContainer>
    );
  }

  const allColumns = getAllColumns();
  const currentBoard = boards[selectedBoardIndex];

  return (
    <>
      <MobileContainer>
        <StickyHeader>
          <HeaderTop>
            <BackButton onClick={onBack}>
              <IconArrowLeft size={14} />
              Back
            </BackButton>
            <HeaderTitle>{subcategoryName} Task Boards</HeaderTitle>
            {(isOwner || isMember) && !isSubmitted && (
              <Menu shadow="md" width={220} withinPortal={true}>
                <Menu.Target>
                  <MenuButton>
                    <IconDotsVertical size={16} />
                  </MenuButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconPlus size={14} />}
                    onClick={handleCreateNewBoard}
                  >
                    New Board
                  </Menu.Item>
                  {currentBoard?.boardDescription && (
                    <Menu.Item
                      icon={<IconInfoCircle size={14} />}
                      onClick={() => {
                        alert(currentBoard.boardDescription);
                      }}
                    >
                      View Description
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  <Menu.Item
                    icon={<IconTrash size={14} />}
                    color="red"
                    onClick={deleteBoard}
                  >
                    Delete Board
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </HeaderTop>
          <BoardTabs>
            {boards.map((board, index) => (
              <BoardTab
                key={board.id}
                active={selectedBoardIndex === index}
                onClick={() => setSelectedBoardIndex(index)}
              >
                {board.boardTitle || `Board ${index + 1}`}
              </BoardTab>
            ))}
          </BoardTabs>
        </StickyHeader>

        {boards.length == 0 && (
          <NoTasks>
            <p>No task boards found.</p>
            <p>Create a task board to get started.</p>
            {(isOwner || isMember) && !isSubmitted && (
              <Button onClick={handleCreateNewBoard} mt="md">
                Create First Board
              </Button>
            )}
          </NoTasks>
        )}

        {(isOwner || isMember) && !isSubmitted && boards.length > 0 && (
          <ColumnActionsBar>
            {isEditingColumns ? (
              <>
                <Button
                  variant="filled"
                  color="green"
                  size="sm"
                  onClick={handleSaveColumnEdits}
                  style={{ flex: 1 }}
                >
                  Done
                </Button>
                <Button
                  variant="filled"
                  color="violet"
                  size="sm"
                  onClick={() => setShowColumnModal(true)}
                >
                  <IconPlus size={14} />
                  Add Column
                </Button>
              </>
            ) : (
              <Button
                variant="filled"
                color="violet"
                size="sm"
                onClick={handleStartEditingColumns}
                fullWidth
              >
                Manage Columns
              </Button>
            )}
          </ColumnActionsBar>
        )}

        <ContentArea>
          {allColumns.map((column, index) => {
            const isExpanded = expandedColumns[column.id];
            const taskCount = column.tasks?.length || 0;
            const isEditingThisColumn =
              isEditingColumns && editingColumnName[column.id] !== undefined;
            return (
              <ColumnSection key={column.id}>
                <ColumnHeader
                  color={column.color}
                  expanded={isExpanded}
                  onClick={() => toggleColumn(column.id)}
                >
                  <ColumnHeaderLeft>
                    <ColumnDot color={column.color} />
                    {isEditingThisColumn ? (
                      // Edit mode: Show text input
                      <TextInput
                        value={editingColumnName[column.id] || column.name}
                        onChange={(e) =>
                          setEditingColumnName((prev) => ({
                            ...prev,
                            [column.id]: e.target.value,
                          }))
                        }
                        autoFocus={index == 0}
                        styles={{
                          input: {
                            padding: "4px 8px",
                            height: "28px",
                            minHeight: "28px",
                            fontSize: "14px",
                            fontWeight: 600,
                          },
                        }}
                      />
                    ) : (
                      // Normal mode: Show column name
                      <ColumnTitle>{column.name}</ColumnTitle>
                    )}
                  </ColumnHeaderLeft>
                  <TaskCount>
                    <span>{taskCount}</span>
                    {isExpanded ? (
                      <IconChevronDown size={20} />
                    ) : (
                      <IconChevronRight size={20} />
                    )}
                  </TaskCount>
                  {isEditingColumns && (
                    // Edit mode: Show delete button
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => handleDeleteColumn(column.id)}
                      styles={{
                        root: {
                          padding: "4px 8px",
                          height: "24px",
                        },
                      }}
                    >
                      <IconTrash size={14} />
                    </Button>
                  )}
                </ColumnHeader>

                {isExpanded && (
                  <TaskList>
                    {taskCount === 0 ? (
                      <NoTasks>No tasks in this column</NoTasks>
                    ) : (
                      column.tasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          onClick={() =>
                            handleTaskClick(task, column.id, column.boardId)
                          }
                        >
                          <TaskTitle>{task.title}</TaskTitle>
                          {task.description && (
                            <TaskDescription>
                              {task.description.length > 100
                                ? `${task.description.substring(0, 100)}...`
                                : task.description}
                            </TaskDescription>
                          )}
                          <TaskMeta>
                            {task.tags && task.tags.length > 0 && (
                              <TaskTags>
                                {task.tags.map((tag, idx) => (
                                  <Tag key={idx} color={tag.color}>
                                    {tag.name}
                                  </Tag>
                                ))}
                              </TaskTags>
                            )}

                            {task.priority && (
                              <div>
                                <PriorityBadge priority={task.priority}>
                                  {task.priority}
                                </PriorityBadge>
                              </div>
                            )}

                            {task.assignees && task.assignees.length > 0 && (
                              <AssigneeSection>
                                <IconUser size={16} color="#666" />
                                {task.assignees.map((assignee) => (
                                  <Badge
                                    key={assignee.id}
                                    size="sm"
                                    variant="light"
                                  >
                                    {assignee.first_name} {assignee.last_name}
                                  </Badge>
                                ))}
                              </AssigneeSection>
                            )}
                          </TaskMeta>
                        </TaskCard>
                      ))
                    )}
                  </TaskList>
                )}
              </ColumnSection>
            );
          })}
        </ContentArea>

        {!isSubmitted && (
          <FloatingAddButton onClick={handleAddTask}>
            <IconPlus size={24} />
          </FloatingAddButton>
        )}
      </MobileContainer>

      {/* Task Modal */}
      {taskModal && (
        <Modal
          opened={true}
          onClose={closeModal}
          title={taskModal.mode === "add" ? "Add Task" : "Edit Task"}
          size="lg"
          fullScreen
        >
          <Box p="md">
            <TextInput
              placeholder="Task Title"
              value={taskModal.task.title}
              onChange={(e) =>
                onTaskModalChange({ field: "title", value: e.target.value })
              }
              mb="md"
              size="lg"
            />

            <Textarea
              placeholder="Description"
              value={taskModal.task.description}
              onChange={(e) =>
                onTaskModalChange({
                  field: "description",
                  value: e.target.value,
                })
              }
              minRows={5}
              mb="md"
              size="lg"
            />

            <Select
              data={[
                { value: "Low", label: "Low" },
                { value: "Medium", label: "Medium" },
                { value: "High", label: "High" },
              ]}
              value={taskModal.task.priority}
              onChange={(value) =>
                onTaskModalChange({ field: "priority", value })
              }
              placeholder="Select Priority"
              mb="md"
              size="lg"
            />
            <Select
              data={getAllColumns(selectedBoardIndex).map((col) => ({
                value: col.id.toString(),
                label: `${col.name}`,
              }))}
              defaultValue={taskModal.columnId?.toString()}
              onChange={(value) =>
                setTaskModal((prev) => ({
                  ...prev,
                  newColumnId: parseInt(value),
                }))
              }
              placeholder="Select Column"
              mb="md"
              size="lg"
              withinPortal={true}
            />

            <MultiSelect
              label="Assignees"
              data={users.map((user) => ({
                value: user.id.toString(),
                label: `${user.first_name} ${user.last_name}`,
              }))}
              value={(taskModal.task.assignees || []).map((a) =>
                a.id.toString()
              )}
              onChange={(selectedIds) => {
                const selected = users.filter((u) =>
                  selectedIds.includes(u.id.toString())
                );
                onTaskModalChange({ field: "assignees", value: selected });
              }}
              placeholder="Select Assignees"
              mb="md"
              size="lg"
            />

            <Group position="right" mt="xl" spacing="md">
              <Button onClick={saveTask} color="blue" size="lg">
                Save
              </Button>
              {taskModal.mode === "edit" && (
                <Button onClick={deleteTask} color="red" size="lg">
                  Delete
                </Button>
              )}
              <Button onClick={closeModal} color="gray" size="lg">
                Cancel
              </Button>
            </Group>
          </Box>
        </Modal>
      )}

      {/* New Board Modal */}
      <Modal
        opened={newBoardModal}
        onClose={closeNewBoardModal}
        title="Create New Board"
        size="lg"
        fullScreen
      >
        <ModalContent>
          <FormField>
            <Label htmlFor="board-title">Board Name</Label>
            <Input
              id="board-title"
              type="text"
              placeholder="Enter board name"
              value={newBoardData.boardTitle}
              onChange={(e) =>
                handleNewBoardChange("boardTitle", e.target.value)
              }
              maxLength={100}
            />
            <HelperText>Leave empty for default name</HelperText>
          </FormField>

          <FormField>
            <Label htmlFor="board-description">Description (Optional)</Label>
            <TextArea
              id="board-description"
              placeholder="Enter board description"
              value={newBoardData.boardDescription}
              onChange={(e) =>
                handleNewBoardChange("boardDescription", e.target.value)
              }
            />
            <HelperText>Leave empty for default description</HelperText>
          </FormField>

          <Group position="right" mt="xl" spacing="md">
            <Button onClick={confirmCreateNewBoard} color="blue" size="lg">
              Create Board
            </Button>
            <Button onClick={closeNewBoardModal} color="gray" size="lg">
              Cancel
            </Button>
          </Group>
        </ModalContent>
      </Modal>

      {/* Edit Column Modal */}
      {showColumnModal && (
        <ColumnModal
          opened={showColumnModal}
          columnName={newColumnName}
          onChange={setNewColumnName}
          onSave={saveNewColumn}
          onClose={() => setShowColumnModal(false)}
        />
      )}
    </>
  );
};

export default MobileTaskBoardView;
